import { useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { isMobile } from 'react-device-detect';
import { Box } from 'reflexbox';
import styled from 'styled-components';
import moment from 'moment';

import { Route, Switch } from '@treams/treams-routes';

import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import CollapseIcon from '../../../kakapo-ui-kit/assets/icons/CollapseIcon';

import DashboardDepartment from './DashboardDepartment';
import DashboardEngagement from './DashboardEngagement';
import DashboardIndividual from './DashboardIndividual';
import DashboardOrganization from './DashboardOrganization';
import DashboardSidebar from './DashboardSidebar';
import useWorkspaceConfiguration from 'packages/kakapo-ui-application/hooks/useWorkspaceConfiguration';
import DashboardEngagementTopics from './DashboardEngagementTopics';
import DashboardEngagementQuestions from './DashboardEngagementQuestions';

const Split = styled.div`
	display: flex;
	height: 100%;
	overflow: hidden;
`;

const Main = styled.div`
	position: relative;
	flex-basis: 0;
	flex-grow: 1;
	flex-shrink: 1;
`;

const Detail = styled(Box)`
	background: ${(props) => props.theme.colors.lightBlueLight};
	border-left: 1px solid ${(props) => props.theme.colors.lightBlue};
	flex-shrink: 0;
	width: 360px;
	max-width: 360px;
`;

const DetailToolbar = styled(Box)`
	background: ${(props) => props.theme.colors.lightBlueLight};
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
`;

const Collapse = styled(Box)<{ collapsed: boolean; narrow?: boolean }>`
	${(props) =>
		props.narrow
			? `
		margin: 0px auto;
		margin-top: -15px;
		transform: rotate(-90deg);`
			: `
		position: absolute;
		top: -7px;
		right: -15px;
	`}
	width: 30px;
	height: 30px;
	border-radius: 15px;
	background: white;
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	cursor: pointer;

	${(props) =>
		props.collapsed
			? props.narrow
				? `
		transform: rotate(90deg);
		margin-top: 24px;
			`
				: `
		transform: rotate(180deg);
		right: 24px;
	`
			: ``}

	&:hover {
		background: ${(props) => props.theme.colors.whiteBlueLight};

		&:active {
			background: ${(props) => props.theme.colors.whiteBlue};
		}
	}
`;

export function DashboardWithoutSidebar() {
	const viewer = useAuthenticatedUser();
	const configuration = useWorkspaceConfiguration();
	const [collapsed, setCollapsed] = useState(isMobile);

	const today = moment().endOf('day');
	const workspaceStartDate =
		configuration.data?.startDate && new Date(configuration.data.startDate);

	const formik = useFormik({
		initialValues: {
			startDate: today.clone().subtract(1, 'month').toDate(),
			endDate: today.toDate(),
			groupIDs: [],
			userIDs: [viewer.id],
		},
		onSubmit: () => {},
	});

	const filters = {
		startDate:
			workspaceStartDate && formik.values.startDate < workspaceStartDate
				? workspaceStartDate
				: formik.values.startDate,
		endDate: formik.values.endDate,
		groupIDs: formik.values.groupIDs,
		userIDs: formik.values.userIDs,
	};

	return (
		<FormikProvider value={formik}>
			<Box style={{ position: 'relative', height: '100%' }}>
				{!collapsed && (
					<DetailToolbar display={['block', 'block', 'none']}>
						<DashboardSidebar filters={filters} />
					</DetailToolbar>
				)}
				<Collapse
					narrow
					display={['block', 'block', 'none']}
					mt={4}
					onClick={() => setCollapsed(!collapsed)}
					collapsed={collapsed}
				>
					<CollapseIcon />
				</Collapse>
				<Split>
					<Main>
						<Switch>
							<Route path={['/analytics', '/v2/analytics']} exact>
								<DashboardOrganization filters={filters} />
							</Route>
							<Route path="/analytics/department" exact>
								<DashboardDepartment filters={filters} />
							</Route>
							<Route path="/analytics/employee" exact>
								<DashboardIndividual filters={filters} />
							</Route>
							<Route path="/analytics/engagement" exact>
								<DashboardEngagement filters={filters} />
							</Route>
							<Route path="/analytics/engagement/topics" exact>
								<DashboardEngagementTopics filters={filters} />
							</Route>
							<Route path="/analytics/engagement/questions" exact>
								<DashboardEngagementQuestions filters={filters} />
							</Route>
						</Switch>
						<Collapse
							display={['none', 'none', 'block']}
							mt={4}
							onClick={() => setCollapsed(!collapsed)}
							collapsed={collapsed}
						>
							<CollapseIcon />
						</Collapse>
					</Main>
					{!collapsed && (
						<Detail display={['none', 'none', 'block']}>
							<DashboardSidebar filters={filters} />
						</Detail>
					)}
				</Split>
			</Box>
		</FormikProvider>
	);
}
